import { useCallback, useRef } from 'react';
import { useDisplayPreferences, useUpdateDisplayPreferences } from '@/services/api/hooks/settings';
import { useTourContext } from '@/contexts/TourProvider';
import { TourDefinitions } from '@/components/tours/TourDefinitions';
import { useQueryClient } from '@tanstack/react-query';
import { useCompanyId } from '@/services/authentication/hooks/user';
import { useCompanyById } from '@/services/api/hooks/companies';

export const useProductTour = (tourId: string) => {
  const { startTour: triggerTour } = useTourContext();
  const queryClient = useQueryClient();
  const hasFired = useRef(false);
  
  const companyId = useCompanyId();
  const company = useCompanyById(companyId || '');

  // Disable throwing on errors for useDisplayPreferences so it doesn't crash if unauthenticated
  const [preferences] = useDisplayPreferences();
  const updatePreferences = useUpdateDisplayPreferences();

  const startTour = useCallback(() => {
    if (!company || !company.isProductToursEnabled) return;
    if (!preferences) return;
    if (hasFired.current) return;

    const completed = preferences.completedTours || [];
    if (completed.includes(tourId)) {
      return; // Tour already seen
    }

    const steps = TourDefinitions[tourId];
    if (!steps) {
      console.warn(`Tour definition not found for tourId: ${tourId}`);
      return;
    }

    hasFired.current = true;

    triggerTour(tourId, steps, () => {
      // Mark as completed on complete or cancel
      const newCompleted = [...completed, tourId];
      updatePreferences.mutate({
        completedTours: newCompleted
      }, {
        onSuccess: () => {
          queryClient.invalidateQueries();
        }
      });
    });
  }, [company, preferences, tourId, updatePreferences, triggerTour, queryClient]);

  return { startTour };
};
